import React, { useState, useRef, useEffect } from "react";
import supabase from "../../../utils/supabase";
import { toast } from "react-toastify";
import { brandingConfig } from "../../../config/branding";

// Platform context for AI
// Run E2 J1: context rewritten for Project Explore (K-5 literacy, EN + ES).
const PLATFORM_CONTEXT = {
  roles: {
    admin: "Administrators who manage the platform, users, and content",
    teacher:
      "K-5 teachers who assign chapters to their classes and track student progress",
    student:
      "K-5 students who read and listen to chapters and complete assessments",
    support:
      "Support staff who help users with technical and educational issues",
  },
  features: {
    chapters:
      "4 seasons x 12 chapters. Each chapter has one episode at 4 tiers (T1 = K-1st grade, T2 = 2nd, T3 = 3rd, T4 = 4th-5th), in English and Spanish, plus an extension activity",
    assessments: "Questions that check reading and listening for each chapter",
    progress: "Tracking student progress on assigned chapters",
  },
};

export default function LiveChatPage() {
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: `Hi there! I'm your AI assistant for the ${brandingConfig.siteTitle} platform.

      If you need to speak with a live person, just type 'connect to live support'`,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isLiveSupport, setIsLiveSupport] = useState(false);
  const messagesEndRef = useRef(null);

  const handleLiveSupportRequest = async () => {
    try {
      setIsLiveSupport(true);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: `You can reach Justin directly. He'll be happy to help you with your questions.`,
          timestamp: new Date(),
        },
      ]);
    } catch (error) {
      console.error("Error requesting live support:", error);
      toast.error("Failed to connect to live support. Please try again.");
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    const userMessage = {
      sender: "user",
      text: input.trim(),
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    // Check for live support request
    if (input.toLowerCase().includes("connect to live support")) {
      setLoading(false);
      handleLiveSupportRequest();
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("openai", {
        body: JSON.stringify({
          prompt: `You are a helpful AI assistant for the ${brandingConfig.siteTitle} platform, a K-5 literacy program in English and Spanish. Financial literacy is the content the stories are about.

Platform Context:
- Roles:
  * Admin: ${PLATFORM_CONTEXT.roles.admin}
  * Teacher: ${PLATFORM_CONTEXT.roles.teacher}
  * Student: ${PLATFORM_CONTEXT.roles.student}
  * Support: ${PLATFORM_CONTEXT.roles.support}

- Features:
  * Chapters: ${PLATFORM_CONTEXT.features.chapters}
  * Assessments: ${PLATFORM_CONTEXT.features.assessments}
  * Progress: ${PLATFORM_CONTEXT.features.progress}

The user asks: "${userMessage.text}"

Provide a clear, concise, and helpful response that:
1. Addresses the specific question about the platform
2. Considers the user's role and context
3. Includes relevant educational content if applicable
4. Suggests appropriate platform features or tools
5. Formats the response with proper paragraphs and bullet points

Keep responses focused on the platform's literacy teaching and its financial literacy stories. Students do not message teachers in the platform.`,
        }),
      });

      if (error) throw error;

      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: data.message,
          timestamp: new Date(),
        },
      ]);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "Sorry, something went wrong. Please try again.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatMessage = (text) => {
    // Split text into paragraphs
    const paragraphs = text.split("\n\n");
    return paragraphs.map((paragraph, index) => {
      // Check if paragraph is a bullet point list
      if (paragraph.trim().startsWith("- ")) {
        const items = paragraph.split("\n");
        return (
          <ul key={index} className="list-disc pl-4 space-y-1">
            {items.map((item, i) => (
              <li key={i}>{item.replace("- ", "")}</li>
            ))}
          </ul>
        );
      }
      return (
        <p key={index} className="mb-2">
          {paragraph}
        </p>
      );
    });
  };

  return (
    <div className="bg-zinc-50 pt-20 min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 text-white text-center px-4">
        <h1 className="text-4xl font-bold mb-4">Live Chat Support</h1>
        <p className="text-lg max-w-2xl mx-auto">
          Talk to our AI assistant 24/7. If you need further help, we'll connect
          you with Justin for personalized support.
        </p>
      </section>

      {/* Chat Container */}
      <section className="py-16 px-6 max-w-3xl mx-auto">
        <div className="bg-white border border-gray-200 rounded-xl shadow p-6">
          <h2 className="text-xl font-semibold text-indigo-700 mb-4">
            {isLiveSupport ? "Live Support Session" : "Start a Conversation"}
          </h2>

          <div className="h-[400px] overflow-y-auto space-y-4 bg-gray-50 p-4 rounded border">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex flex-col ${
                  msg.sender === "bot" ? "items-start" : "items-end"
                }`}
              >
                <div
                  className={`text-sm px-4 py-2 rounded-lg max-w-[80%] ${
                    msg.sender === "bot"
                      ? "bg-indigo-100 text-indigo-800"
                      : "bg-indigo-600 text-white"
                  }`}
                >
                  {formatMessage(msg.text)}
                </div>
                <span className="text-xs text-gray-500 mt-1">
                  {msg.timestamp.toLocaleTimeString()}
                </span>
              </div>
            ))}
            {loading && (
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.4s" }}
                ></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="mt-4 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              className="flex-1 border border-gray-300 rounded px-4 py-2 text-sm"
              placeholder="Type your message... "
            />
            <button
              onClick={sendMessage}
              disabled={loading}
              className="bg-indigo-600 text-white px-4 py-2 rounded text-sm hover:bg-indigo-700 disabled:opacity-50"
            >
              Send
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
